import { useContext, useEffect, useState } from 'react';
import axios from "axios";
import { navigate } from "raviger";
import { Form, Formik, } from 'formik';
import {
  Button,
  ButtonGroup,
  Card,
  Col,
  FormGroup,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowAltCircleLeft,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import * as Yup from 'yup';
import { DateTimePicker, DropDown, TextInput } from '../components/Form';
import { SystemErrorContext } from '../components/SystemError';
import { AuthContext } from "../accounts/AccountsReducer";
import Patient from './components/Patient';

const MedicalNoteForm = (props) => {

  const { state } = useContext(AuthContext);
  const { setShowSystemError } = useContext(SystemErrorContext);

  const [data, setData] = useState({
    diagnosis: [],
    open: new Date(),
    medical_record: props.medrecordid,
    note: '',
    noted_by: state.user.id
  });
  const [medRecordData, setMedRecordData] = useState({id:'', medical_plan:'', noted_by:null, animal_object: {id:'', name:'', species_string:'', medical_notes:''}, vet_requests:[], exams:[]});
  const [assigneeChoices, setAssigneeChoices] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const deleteMedicalNote = () => {
    axios.delete('/vet/api/medicalnote/' + props.id + '/')
      .finally(() => navigate('/' + props.organization + '/' + props.incident + '/vet/medrecord/' + data.medical_record));
  }

  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    const fetchMedRecord = async (id) => {
      // Fetch MedRecord data.
      await axios.get('/vet/api/medrecord/' + id + '/', {
        cancelToken: source.token,
      })
      .then(medResponse => {
        if (!unmounted) {
          setMedRecordData(medResponse.data);
        }
      })
      .catch(error => {
        if (axios.isCancel(error)) {
          console.log('Request canceled gracefully:', error.message);
        } else if (error.response)
        {
          setShowSystemError(true);
        }
      });
    };

    if (props.id) {
      // Fetch note data.
      axios.get('/vet/api/medicalnote/' + props.id + '/', {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          setData(response.data);
          fetchMedRecord(response.data.medical_record)
        }
      })
      .catch(error => {
        if (axios.isCancel(error)) {
          console.log('Request canceled gracefully:', error.message);
        } else if (error.response)
        {
          setShowSystemError(true);
        }
      });
    }
    else {
      fetchMedRecord(props.medrecordid);
    }

    const fetchAssignees = async () => {
      // Fetch assignee data.
      await axios.get('/accounts/api/user/?vet=true&organization=' + state.organization.id, {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          let options = [];
          response.data.forEach(function(person) {
            options.unshift({value: person.id, label: person.first_name + ' ' + person.last_name})
          });
          setAssigneeChoices(options);
        }
      })
      .catch(error => {
        if (axios.isCancel(error)) {
          console.log('Request canceled gracefully:', error.message);
        } else if (error.response)
        {
          setShowSystemError(true);
        }
      });
    };
    fetchAssignees();

    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [props.medrecordid]);

  return (
    <>
    <Formik
      initialValues={data}
      enableReinitialize={true}
      validationSchema={Yup.object({
        open: Yup.string().required('Required'),
        noted_by: Yup.string().required('Required'),
        note: Yup.string().required('Required').max(3000, 'Maximum character limit of 3000.'),
      })}
      onSubmit={(values, { setSubmitting }) => {
        if (props.id) {
          axios.put('/vet/api/medicalnote/' + props.id + '/', values)
          .then(response => {
            navigate('/' + props.organization + '/' + props.incident + '/vet/medrecord/' + data.medical_record);
          })
          .catch(error => {
            setShowSystemError(true);
          });
        }
        else {
          axios.post('/vet/api/medicalnote/', values)
          .then(response => {
            navigate('/' + props.organization + '/' + props.incident + '/vet/medrecord/' + props.medrecordid);
          })
          .catch(error => {
            setShowSystemError(true);
          });
        }
        setSubmitting(false);
      }}
    >
      {formikProps => (
        <Card border="secondary" className="mt-3">
          <Card.Header as="h5" className="ps-3">
            <span style={{ cursor: 'pointer' }} onClick={() => window.history.back()} className="me-3"><FontAwesomeIcon icon={faArrowAltCircleLeft} size="lg" inverse /></span>
            Daily Medical Note Form
              {props.id ? <OverlayTrigger
                key={"delete-note"}
                placement="bottom"
                overlay={
                  <Tooltip id={`tooltip-delete-note`}>
                    Delete daily medical note
                  </Tooltip>
                }
              >
                <FontAwesomeIcon icon={faTimes} onClick={() => setShowModal(true)} style={{cursor:'pointer'}} className='ms-1 float-end' size="lg" inverse />
              </OverlayTrigger> : ""}
          </Card.Header>
          <Patient animal={medRecordData.animal_object} vet_request={null} organization={props.organization} incident={props.incident} medical_plan={medRecordData.medical_plan} />
          <Card.Body>
            <Form>
              <FormGroup>
                <Row className="gx-2">
                  <Col xs={"4"}>
                    {assigneeChoices.length > 0 ? <DropDown
                      label="Note By*"
                      id="noted_by"
                      name="noted_by"
                      type="text"
                      options={assigneeChoices}
                      isClearable={true}
                      // defaultValue={state.user.id}
                      onChange={(instance) => {
                        formikProps.setFieldValue("noted_by", instance === null ? '' : instance.value);
                      }}
                    /> : ""}
                  </Col>
                </Row>
                <Row className="mt-3 gx-2">
                  <DateTimePicker
                    label="Day"
                    name="open"
                    id="open"
                    xs="4"
                    onChange={(date, dateStr) => {
                      formikProps.setFieldValue("open", dateStr.split(" ")[0])
                    }}
                    value={formikProps.values.open||new Date()}
                    data-enable-time={false}
                    disabled={false}
                    clearable={false}
                  />
                </Row>
                <Row className="mt-3 gx-2">
                  <TextInput
                    as="textarea"
                    label="Daily Note"
                    name="note"
                    id="note"
                    xs="12"
                    rows={4}
                  />
                </Row>
              </FormGroup>
            </Form>
          </Card.Body>
          <ButtonGroup>
            <Button type="button" className="btn btn-primary" onClick={() => { formikProps.submitForm() }}>Save</Button>
          </ButtonGroup>
        </Card>
      )}
    </Formik>
    <Modal show={showModal} onHide={() => setShowModal(false)}>
      <Modal.Header closeButton>
        <Modal.Title>Confirm Medical Note Deletion</Modal.Title>
      </Modal.Header>
      <Modal.Body>Are you sure you want to delete this daily medical note?</Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={() => deleteMedicalNote()}>
          Yes
        </Button>
        <Button variant="secondary" onClick={() => setShowModal(false)}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default MedicalNoteForm;
